import { XMLParser } from 'fast-xml-parser';

/** A parsed XML node: attributes on `@`, children by tag name. */
export type Node = Record<string, any>;

const parser = new XMLParser({
  ignoreAttributes: false,
  attributeNamePrefix: '',
  attributesGroupName: '@',
  parseAttributeValue: false,
  parseTagValue: false,
  trimValues: true,
  isArray: () => false,
});

export function parseXml(text: string): Node {
  return parser.parse(text);
}

/** Children under `tag`, always as an array (fast-xml-parser collapses single children). */
export function kids(node: Node | undefined, tag: string): Node[] {
  if (!node) return [];
  const v = node[tag];
  if (v === undefined || v === null) return [];
  return Array.isArray(v) ? v : [v];
}

/** The single child under `tag`, or undefined. */
export function kid(node: Node | undefined, tag: string): Node | undefined {
  const list = kids(node, tag);
  return list[0];
}

/** Follow a `A/B/C` path of single children. */
export function path(node: Node | undefined, p: string): Node | undefined {
  let cur = node;
  for (const seg of p.split('/')) {
    cur = kid(cur, seg);
    if (!cur) return undefined;
  }
  return cur;
}

export function attr(node: Node | undefined, name: string): string | undefined {
  const a = node?.['@'];
  const v = a?.[name];
  return v === undefined || v === null ? undefined : String(v);
}

export function num(node: Node | undefined, name: string, fallback = 0): number {
  const v = attr(node, name);
  if (v === undefined) return fallback;
  const n = Number(v);
  return Number.isFinite(n) ? n : fallback;
}

export function bool(node: Node | undefined, name: string, fallback = false): boolean {
  const v = attr(node, name);
  if (v === undefined) return fallback;
  return v.toLowerCase() === 'true';
}

/** `<Position x= y= z=>` / `<Position X= Y=>` — the XML mixes cases by element. */
export function xy(node: Node | undefined): { x: number; y: number } {
  if (!node) return { x: 0, y: 0 };
  const a = node['@'] ?? {};
  const x = Number(a.x ?? a.X ?? 0);
  const y = Number(a.y ?? a.Y ?? 0);
  return { x: Number.isFinite(x) ? x : 0, y: Number.isFinite(y) ? y : 0 };
}

export function xyz(node: Node | undefined, fallback = 0): { x: number; y: number; z: number } {
  if (!node) return { x: fallback, y: fallback, z: fallback };
  const a = node['@'] ?? {};
  const g = (k: string, K: string) => {
    const n = Number(a[k] ?? a[K] ?? fallback);
    return Number.isFinite(n) ? n : fallback;
  };
  return { x: g('x', 'X'), y: g('y', 'Y'), z: g('z', 'Z') };
}
